// Payouts queue: flagged first, then oldest. j/k move, a approve (a confirm, then 10 s to undo), r reject with 1-5,
// h hold with a note, enter open, u undo, esc close. The approver never edits amount, partner or destination.
// Manual payouts (CHANGE-003): an approved_manual row is sent by hand, then marked paid (reference) or failed (reason).
// CHANGE-070: the cursor stays on its row by id, so a row leaving never moves it onto the next payout; a payout being
// approved is locked; every approval keeps its own undo; the list polls so hand-sent rows appear on time.
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, cents, points } from '../../lib'
import { Button, Busy, Err, Fresh, Page, Section, Sources, Table, ago, dayOf, errorText, useCursor, useKeys, useKit, type Column } from './kit'
import { CompanyLink, Facts, PartnerLink, useStaffAction, when } from './common'

type Ref = { id: string; name: string }
type Row = {
  redemption_id: string
  status: string
  company: Ref
  requester: { user_id: string; email: string | null }
  days_since_join: number
  partner: Ref
  reported_spend_cents: number
  rebated_cents: number
  points: number
  amount_cents: number
  balance_after: number
  funded_by: { cold_start: number | null; partner: number | null }
  bank_name: string | null
  bank_last4: string | null
  first_use: boolean
  flags: { code: string; reason: string }[]
  assignee: string | null
  created_at: string
  note?: string | null
}
type Paused = { on: boolean; reason: string | null; at: string | null; by: string | null }
type Queue = {
  rows: Row[]; reject_reasons: Record<string, string>; undo_seconds: number; me: string
  payouts_mode?: 'stripe' | 'manual'; paused?: Paused; hidden_own?: number; read_at?: string
}
type Mode = 'idle' | 'reject' | 'hold' | 'open' | 'paid' | 'failed'
type Undo = { id: string; label: string; until: number }

const KEY = ['staff', 'payouts']
const rowKey = (r: Row) => r.redemption_id
const isManual = (r: Row | null | undefined) => r?.status === 'approved_manual'
// all points released at this partner for the company, not this payout's own funding (inventory 6.35)
const released = (r: Row) => (!r.funded_by || r.funded_by.cold_start === null || r.funded_by.partner === null ? '–'
  : `${points(r.funded_by.cold_start ?? 0)} from our launch budget, ${points(r.funded_by.partner ?? 0)} from the partner, all payouts`)
/** One line per truth: what a new approval does, and what the rows already set to go by hand are. */
function modeLine(mode: Queue['payouts_mode'], byHand: number): string {
  const first = mode === 'manual' ? 'Manual mode: approved payouts are sent by hand' : mode === 'stripe' ? 'Stripe mode: new approvals send by Stripe' : 'Payout mode not read'
  return mode === 'manual' || !byHand ? first : `${first}. ${byHand} already approved ${byHand === 1 ? 'goes' : 'go'} by hand`
}

const bank = (r: Row, mode: Queue['payouts_mode']) =>
  r.bank_name ? `${r.bank_name} ••${r.bank_last4}` : mode === 'manual' ? 'By hand, ask the requester' : 'No bank yet'

export function PayoutsPage() {
  const qc = useQueryClient()
  const kit = useKit()
  const q = useQuery({ queryKey: KEY, queryFn: () => api<Queue>('GET', '/api/staff/payouts'), refetchInterval: 5_000 })
  const [gone, setGone] = useState<string[]>([])   // approved here: off the list at once, back if undone
  const payMode = q.data?.payouts_mode
  const paused = !!q.data?.paused?.on
  const [mode, setMode] = useState<Mode>('idle')
  const [reason, setReason] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [undos, setUndos] = useState<Undo[]>([])
  // Hidden only while its undo shows. After that the row shows as it stands: send by hand, or held if the job bounced it.
  const rows = (q.data?.rows ?? []).filter((r) => !(gone.includes(r.redemption_id) && undos.some((u) => u.id === r.redemption_id) && r.status !== 'approved_manual'))
  const [locked, setLocked] = useState<string[]>([])
  const busy = useRef(new Set<string>())       // ids with an approve or undo in flight, checked before any await
  const asking = useRef(false)                  // a confirm is open
  const noteRef = useRef<HTMLInputElement>(null)

  const close = () => { setMode('idle'); setReason(null); setNote('') }
  const enter = () => {
    if (mode === 'reject' && reason) return doReject()
    if (mode === 'hold' && note.trim()) return doHold()
    setMode((m) => (m === 'open' ? 'idle' : 'open'))
  }
  const { cur, row, setCur } = useCursor(rows, rowKey, enter)
  const manual = isManual(row)
  // a different row closes any open panel
  useEffect(() => { close() }, [cur])

  const reject = useStaffAction<{ reason_code: number; note: string }>((id) => `/api/staff/redemptions/${id}/reject`, [KEY])
  const hold = useStaffAction<{ note: string }>((id) => `/api/staff/redemptions/${id}/hold`, [KEY])
  const assign = useStaffAction((id) => `/api/staff/redemptions/${id}/assign`, [KEY])
  const markPaid = useStaffAction<{ reference: string }>((id) => `/api/staff/redemptions/${id}/mark-paid`, [KEY])
  const markFailed = useStaffAction<{ reason: string }>((id) => `/api/staff/redemptions/${id}/mark-failed`, [KEY])

  const lock = (id: string, on: boolean) => {
    if (on) busy.current.add(id); else busy.current.delete(id)
    setLocked(Array.from(busy.current))
  }
  const fail = (e: unknown) => kit.toast(errorText(e), { bad: true, ms: 6_000 })

  async function doApprove() {
    const r = row
    if (isManual(r)) { kit.toast('Already approved. This one is sent by hand, so mark it paid or failed', { ms: 6_000 }); return }
    if (!r || paused || asking.current || busy.current.has(r.redemption_id)) return
    asking.current = true
    const ok = await kit.confirm({
      title: `Approve ${cents(r.amount_cents)}`,
      confirm: `Approve ${cents(r.amount_cents)}`,
      body: <Facts testid="staff-approve-facts" items={[
        ['Company', r.company.name], ['Amount', `${cents(r.amount_cents)} for ${points(r.points)} points`],
        ['Partner', r.partner.name], ['To', bank(r, payMode)],
        ['Then', payMode === 'manual' ? 'Moves to send by hand in 10 seconds' : 'Sends in 10 seconds unless undone'],
        ...(r.flags.length ? [['Flags', r.flags.map((f) => f.reason).join('. ')] as [string, string]] : []),
      ]} />,
    })
    asking.current = false
    if (!ok.ok || busy.current.has(r.redemption_id)) return
    lock(r.redemption_id, true)
    try {
      await api('POST', `/api/staff/redemptions/${r.redemption_id}/approve`)
      setGone((g) => [...g, r.redemption_id])
      const secs = q.data?.undo_seconds ?? 10
      setUndos((u) => [...u, { id: r.redemption_id, label: `${r.company.name}, ${cents(r.amount_cents)}`, until: Date.now() + secs * 1000 }])
    } catch (e) { fail(e) } finally {
      lock(r.redemption_id, false)
      qc.invalidateQueries({ queryKey: KEY })
    }
  }

  async function doUndo(u?: Undo) {
    const it = u ?? undos[undos.length - 1]
    if (!it || busy.current.has(it.id)) return
    setUndos((x) => x.filter((y) => y.id !== it.id))
    lock(it.id, true)
    try {
      await api('POST', `/api/staff/redemptions/${it.id}/undo`)
      setGone((g) => g.filter((x) => x !== it.id))
      kit.toast(`Undone: ${it.label}`)
    } catch (e) { fail(e) } finally {
      lock(it.id, false)
      qc.invalidateQueries({ queryKey: KEY })
    }
  }

  function doReject(code = reason) {
    if (!row || !code) return
    reject.mutate({ id: row.redemption_id, body: { reason_code: code, note } }, { onSuccess: close, onError: fail })
  }
  function doHold() {
    if (!row || !note.trim()) return
    hold.mutate({ id: row.redemption_id, body: { note } }, { onSuccess: close, onError: fail })
  }
  function doMark() {
    if (!row || !note.trim() || (mode !== 'paid' && mode !== 'failed')) return
    const id = row.redemption_id
    const done = { onSuccess: close, onError: fail }
    if (mode === 'paid') markPaid.mutate({ id, body: { reference: note.trim() } }, done)
    else markFailed.mutate({ id, body: { reason: note.trim() } }, done)
  }
  const openNote = (m: Mode) => { setNote(''); setReason(null); setMode(m); setTimeout(() => noteRef.current?.focus()) }

  const keys: Record<string, () => void> = {
    a: () => void doApprove(), u: () => void doUndo(), Escape: close,
    r: () => { if (row && !manual) setMode('reject') },
    h: () => { if (row && !manual) openNote('hold') },
  }
  for (const n of ['1', '2', '3', '4', '5']) keys[n] = () => { if (mode === 'reject') { setReason(Number(n)); setTimeout(() => noteRef.current?.focus()) } }
  useKeys(keys)

  // undo rows count down, then leave
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!undos.length) return
    const t = setInterval(() => { setNow(Date.now()); setUndos((u) => u.filter((x) => x.until > Date.now())) }, 250)
    return () => clearInterval(t)
  }, [undos.length])

  if (q.isLoading) return <Busy what="Loading payouts" />
  if (q.error) return <Page title="Payouts"><Err error={q.error} /></Page>
  const d = q.data!
  const reasons = d.reject_reasons ?? {}
  const readAt = d.read_at ? Date.parse(d.read_at) : Date.now()

  const columns: Column<Row>[] = [
    { key: 'company', head: 'Company', primary: true, nowrap: true, cell: (r) => (
      <><CompanyLink id={r.company.id} name={r.company.name} />
        {isManual(r) ? <span className="st-warn" data-testid="staff-row-send-by-hand"> send by hand</span> : null}
        {r.flags.length ? <span className="st-phone-text st-warn" data-testid="staff-row-flag-count"> {r.flags.length} {r.flags.length === 1 ? 'flag' : 'flags'}</span> : null}
        {r.status === 'held' ? <span className="st-warn"> held</span> : null}
        {locked.includes(r.redemption_id) ? <span className="st-muted"> approving</span> : null}
        <span className="st-pay-sub">{r.requester.email ?? 'requester no longer an admin'}, joined {r.days_since_join} d ago</span></>
    ) },
    { key: 'amount', head: 'Cash', num: true, cell: (r) => <Link className="st-link" to={`/staff/payout/${r.redemption_id}`} data-testid="staff-row-amount">{cents(r.amount_cents)}</Link> },
    { key: 'points', more: true, head: 'Points', num: true, cell: (r) => <span data-testid="staff-row-points">{points(r.points)}</span> },
    { key: 'partner', more: true, head: 'Partner', cell: (r) => <PartnerLink id={r.partner.id} name={r.partner.name} /> },
    { key: 'asked', more: true, head: 'Requested', nowrap: true, cell: (r) => <span title={when(r.created_at)}>{dayOf(r.created_at)}</span> },
    { key: 'age', more: true, head: 'Age', num: true, cell: (r) => {
      const h = (readAt - Date.parse(r.created_at)) / 3_600_000
      return <span className={h > 48 ? 'st-warn' : undefined} title={`At ${when(d.read_at)}`}>{ago(r.created_at, readAt)}</span>
    } },
    { key: 'spend', more: true, head: 'Spend there', num: true, cell: (r) => cents(r.reported_spend_cents) },
    { key: 'rebated', more: true, head: 'Paid before', num: true, cell: (r) => cents(r.rebated_cents) },
    { key: 'bal', more: true, head: 'Spendable', num: true, cell: (r) => <span className={r.balance_after < 0 ? 'st-bad' : undefined}>{points(r.balance_after)}</span> },
    { key: 'bank', more: true, head: 'Bank', nowrap: true, cell: (r) => <span data-testid="staff-row-bank">{bank(r, payMode)}{r.first_use ? ', first use' : ''}</span> },
    { key: 'flags', more: true, head: 'Flags', cell: (r) => (
      <span className="st-pay-flags" data-testid="staff-row-flags">{r.flags.length === 0 ? <span className="st-muted">none</span>
        : r.flags.map((f) => <span key={f.code} className="st-pay-flag" title={f.code}>{f.reason}</span>)}</span>
    ) },
    { key: 'taken', more: true, head: 'Assigned to', cell: (r) => r.assignee ? (r.assignee === d.me ? 'You' : 'Someone else') : (
      <Button tone="quiet" data-testid="staff-assign" onClick={() => assign.mutate({ id: r.redemption_id }, { onError: fail })}>Assign to me</Button>
    ) },
  ]

  const rowLocked = !!row && locked.includes(row.redemption_id)
  return (
    <Page title="Payouts" testid="staff-payouts"
      sub={<Sources>
        <Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} label="Queue" live limit={60} testid="staff-payouts-fresh" />
        <span className="st-fresh" data-testid="staff-payouts-mode">{modeLine(payMode, rows.filter(isManual).length)}</span>
      </Sources>}
      right={<span className="st-faint st-pay-keys">j k move, a approve, r reject, h hold, enter open, u undo. Send-by-hand rows are already approved, so a does nothing there</span>}>
      {paused ? (
        <div className="st-banner bad" role="alert" data-testid="staff-payouts-paused">
          Payouts are paused, so approve is blocked. {d.paused?.reason ? `${d.paused.reason}. ` : ''}
          Set by {d.paused?.by ?? 'unknown'}{d.paused?.at ? `, ${when(d.paused.at)}` : ''}. Nothing in the app clears it yet.
        </div>
      ) : null}
      {d.hidden_own ? (
        <p className="st-note" data-testid="staff-payouts-hidden-own">
          {d.hidden_own === 1 ? '1 payout at your own company is' : `${d.hidden_own} payouts at your own company are`} not shown. Another founder decides {d.hidden_own === 1 ? 'it' : 'them'}.
        </p>
      ) : null}

      <Table rows={rows} columns={columns} rowKey={rowKey} cur={cur} testid="staff-queue"
        onOpen={(r) => setCur(rowKey(r))} empty="No payouts waiting."
        rowAttrs={(r) => ({ 'data-testid': 'staff-queue-row', 'aria-selected': cur === rowKey(r), 'data-status': r.status })} />

      {row ? (
        <div className="st-pay-actions" data-testid="staff-pay-actions">
          <span className="st-strong st-pay-which">{row.company.name}, {cents(row.amount_cents)}</span>
          {manual ? (
            <>
              <Button tone="primary" data-testid="staff-mark-paid" onClick={() => openNote('paid')}>Mark paid</Button>
              <Button data-testid="staff-mark-failed" onClick={() => openNote('failed')}>Mark failed</Button>
              <Button tone="quiet" data-testid="staff-open" keyHint="enter" onClick={() => setMode(mode === 'open' ? 'idle' : 'open')}>Open</Button>
            </>
          ) : (
            <>
              <Button tone="primary" data-testid="staff-approve" keyHint="a" disabled={paused || rowLocked} title={paused ? 'Payouts are paused' : undefined} onClick={() => void doApprove()}>Approve</Button>
              <Button data-testid="staff-reject" keyHint="r" disabled={rowLocked} onClick={() => setMode('reject')}>Reject</Button>
              <Button data-testid="staff-hold" keyHint="h" disabled={rowLocked} onClick={() => openNote('hold')}>Hold</Button>
              <Button tone="quiet" data-testid="staff-open" keyHint="enter" onClick={() => setMode(mode === 'open' ? 'idle' : 'open')}>Open</Button>
            </>
          )}
          {!row.assignee ? <Button tone="quiet" className="st-phone-only" data-testid="staff-assign-bar" onClick={() => assign.mutate({ id: row.redemption_id }, { onError: fail })}>Assign to me</Button> : null}
        </div>
      ) : rows.length ? <p className="st-note"><span className="st-desk-text">Pick a payout with j and k, or click a row.</span><span className="st-phone-text">Tap a payout.</span></p> : null}

      {row && manual ? (
        <p className="st-note" data-testid="staff-manual-hint">
          Approved for a manual payout. Email {row.requester.email ?? 'the requester'} for bank details, send {cents(row.amount_cents)} by
          hand, then mark it paid with the bank reference, or failed with the reason.
        </p>
      ) : null}

      {row && mode === 'reject' ? (
        <Section title="Reject: press 1 to 5 for a reason" testid="staff-reject-panel">
          <div className="st-stack">
            {Object.entries(reasons).map(([code, label]) => (
              <label key={code} className="st-row">
                <input type="radio" name="reason" checked={reason === Number(code)} data-testid={`staff-reject-reason-${code}`}
                  onChange={() => setReason(Number(code))} /> {code}. {label}
              </label>
            ))}
            <input ref={noteRef} className="st-input" placeholder="Note, optional" value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="staff-reject-note" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doReject() } if (e.key === 'Escape') close() }} />
            <div className="st-row">
              <Button tone="danger" disabled={!reason || reject.isPending} data-testid="staff-reject-confirm" onClick={() => doReject()}>Reject {cents(row.amount_cents)}</Button>
              <Button tone="quiet" onClick={close}>Cancel</Button>
            </div>
          </div>
        </Section>
      ) : null}

      {row && mode === 'hold' ? (
        <Section title="Hold: why" testid="staff-hold-panel">
          <div className="st-row">
            <input ref={noteRef} className="st-input st-pay-note" placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="staff-hold-note" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doHold() } if (e.key === 'Escape') close() }} />
            <Button disabled={!note.trim() || hold.isPending} data-testid="staff-hold-confirm" onClick={doHold}>Hold</Button>
            <Button tone="quiet" onClick={close}>Cancel</Button>
          </div>
        </Section>
      ) : null}

      {row && manual && (mode === 'paid' || mode === 'failed') ? (
        <Section title={mode === 'paid' ? `Mark ${cents(row.amount_cents)} paid to ${row.company.name}` : 'Mark failed: the points go back to the company'} testid="staff-mark-panel">
          <div className="st-row">
            <input ref={noteRef} className="st-input st-pay-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={mode === 'paid' ? 'Bank reference or note' : 'Reason'} data-testid="staff-mark-note"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doMark() } if (e.key === 'Escape') close() }} />
            <Button tone={mode === 'paid' ? 'primary' : 'danger'} disabled={!note.trim() || markPaid.isPending || markFailed.isPending}
              data-testid="staff-mark-confirm" onClick={doMark}>{mode === 'paid' ? `Mark ${cents(row.amount_cents)} paid` : 'Mark failed'}</Button>
            <Button tone="quiet" onClick={close}>Cancel</Button>
          </div>
        </Section>
      ) : null}

      {row && mode === 'open' ? <Detail id={row.redemption_id} mode={payMode} /> : null}

      {undos.length ? (
        <div className="st-pay-undos" data-testid="staff-undo-bar" aria-live="polite">
          {undos.map((u) => {
            const left = Math.max(0, Math.ceil((u.until - now) / 1000))
            return (
              <div key={u.id} className="st-toast" data-testid="staff-undo-item">
                <span>Approved {u.label}. {payMode === 'manual' ? `Moves to send by hand in ${left}s` : `Sends in ${left}s`}</span>
                <button type="button" data-testid="staff-undo" onClick={() => void doUndo(u)}>Undo</button>
              </div>
            )
          })}
        </div>
      ) : null}
    </Page>
  )
}

type AuditRow = { id: string; at: string; action: string; actor_user_id: string | null; note: string | null }
const ACTION: Record<string, string> = {
  'redemption.request': 'Requested', 'redemption.monthly': 'Requested, monthly payout', 'redemption.approve': 'Approved', 'redemption.undo': 'Approval undone',
  'redemption.reject': 'Rejected', 'redemption.hold': 'Held', 'redemption.assign': 'Assigned',
  'payout.manual_paid': 'Marked paid', 'payout.manual_failed': 'Marked failed',
}

function Detail({ id, mode }: { id: string; mode: Queue['payouts_mode'] }) {
  const q = useQuery({
    queryKey: ['staff', 'redemption', id],
    queryFn: () => api<{ row: Row; audit: AuditRow[] }>('GET', `/api/staff/redemptions/${id}`),
  })
  if (q.isLoading) return <Busy />
  if (q.error) return <Err error={q.error} />
  const r = q.data!.row
  return (
    <Section title={`${r.company.name} at ${r.partner.name}`} testid="staff-detail"
      right={<span className="st-row"><Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} live /><Link className="st-link" to={`/staff/payout/${r.redemption_id}`}>Payout record</Link></span>}>
      <div className="st-two">
        <Facts items={[
          ['Requested', `${when(r.created_at)} by ${r.requester.email ?? 'someone no longer an admin'}`],
          ['Cash', `${cents(r.amount_cents)} for ${points(r.points)} points`],
          ['Reported spend at partner', `${cents(r.reported_spend_cents)}, ${cents(r.rebated_cents)} paid out before`],
          ['Bank', `${bank(r, mode)}${r.first_use ? ', first use' : ''}`],
          ['Company joined', `${r.days_since_join} days ago`],
          ['Released at this partner', released(r)],
          ['Flags', r.flags.map((f) => f.reason).join('. ') || 'none'],
          ['Company', <CompanyLink key="c" id={r.company.id} name={r.company.name} />],
        ]} />
        <Table rows={q.data!.audit} rowKey={(a) => a.id} flat empty="No actions yet." columns={[
          { key: 'at', head: 'When', nowrap: true, cell: (a) => when(a.at) },
          { key: 'what', head: 'What', cell: (a) => ACTION[a.action] ?? a.action },
          { key: 'note', head: 'Note', cell: (a) => a.note },
        ]} />
      </div>
    </Section>
  )
}
